import { useContext } from 'react';
import { isEmpty } from 'lodash';
import { useRouter } from 'next/router';
import getHeadingTag from 'helpers/getHeadingTag';
import getHeadingSkin from 'helpers/getHeadingSkin';
import { DEFAULT_DOMAIN } from 'prismic/config';
import { DomainContext } from 'contexts/domain/DomainContext';
import { styled } from '@nx-kit/styling';
import { Heading } from 'components/atoms/Heading';
import { RichText } from 'components/atoms/RichText';
import Container from 'components/layout/Grid/Container';
import { Breadcrumb } from 'components/molecules/Breadcrumb';
import { Box, ImageColumn, PictureStyled, Subheadline } from './Hero.shared.styles';
import { HeroShareButtons } from './partials/HeroShareButtons';
import { DateTimeInfo } from './partials/DateTimeInfo';
import HeroHomepageSlider from './partials/HeroHomepageSlider';
import { HeroProps } from './Hero.types';
import { DateContainer } from './HeroCourse.styles';

const ImpliedDash = () => <span className="visually-hidden">{' – '}</span>;

const StoererButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 2rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;

background: rgb(230, 51, 18) !important;
color: #fff !important;

  border: none;
  margin-top: 1.5rem;
  position: relative;
  cursor: pointer;
  text-decoration: none;

  transition: background 0.3s ease;

  &:hover {
    background: rgb(200, 45, 16);
  }

  &::after {
    content: none;
  }
  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
    font-size: 1.1rem;
    padding: 1.2rem;
  }
`;

const HeadingWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

export const Hero: React.FC<HeroProps> = ({
  className,
  children,
  primary: {
    headline,
    subheadline,
    intro_text,
    image,
    big_image,
    show_breadcrumb,
    show_social_sharing,
    times,
    button_text,
    button_link,
  },
}) => {
  const { asPath } = useRouter();
  const {
    computed: { isCourse },
  } = useContext(DomainContext);
  const hasImage = !isEmpty(image);
  const hasIntroText = (intro_text?.[0]?.text && intro_text?.[0].text.length > 0) ?? false;

  const normalizedLink =
  typeof button_link === "string"
    ? button_link
    : button_link?.url || null;

  // The homepage hero is its own full-bleed layout over the background video,
  // so it takes over completely instead of rendering the standard Container.
  //
  // ballenbergkurse.ch serves its homepage on `/` as well, so the path alone
  // cannot tell the two sites apart — only the domain can. Without the isCourse
  // guard the course homepage renders this layout with no video behind it,
  // since PageBackground (correctly) withholds the video there.
  const isHomepage = (asPath === '/' || asPath === `/${DEFAULT_DOMAIN}`) && !isCourse;

  if (isHomepage) {
    return (
      <HeroHomepageSlider
        className={className}
        headline={headline}
        subheadline={subheadline}
        buttonText={button_text}
        link={normalizedLink}
      />
    );
  }

  return (
    <Container className={className}>
      <Box
        isBox={hasImage}
        start={1}
        span={hasImage ? { xs: 12, md: 5 } : { xs: 12, md: 7 }}
        row={hasImage ? { xs: 2, md: 1 } : {}}
      >
        {show_breadcrumb && <Breadcrumb />}
        <HeadingWrapper>
          <Heading
            skin={getHeadingSkin(headline?.[0]?.type)}
            elementType={getHeadingTag(headline?.[0]?.type)}
          >
            {!show_breadcrumb && subheadline && (
              <>
                <Subheadline elementType="span" skin="400">
                  {subheadline}
                </Subheadline>
                <ImpliedDash />
              </>
            )}
            {headline?.[0]?.text}
          </Heading>
        </HeadingWrapper>

{normalizedLink && button_text && (
  <StoererButton
    href={normalizedLink}
    target="_blank"
    rel="noopener noreferrer"
  >
    {button_text} ➝
  </StoererButton>
)}
        {times && (
          <DateContainer>
            {times.map((date) => (
              <DateTimeInfo
                key={`${date.nextDate}`}
                date={date.nextDate}
                timeslot={date.nextTimeslot}
                location={date.location}
                house_nr={date.house_nr}
                locationName={date.locationName}
                ort={date.ort}
              />
            ))}
          </DateContainer>
        )}

        {hasIntroText && <RichText rich_text={intro_text} />}

        {children && <div>{children}</div>}
        {show_social_sharing && <HeroShareButtons />}
      </Box>

      {hasImage && (
        <ImageColumn
          start={{ xs: 1, md: 5 }}
          span={{ xs: 12, md: 8 }}
          row={{ xs: 1, md: 1 }}
          isBig={big_image}
        >
          <PictureStyled objectFit image={image} />
        </ImageColumn>
      )}
    </Container>
  );
};
